import { useLayoutEffect, useState } from "react";

import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { isEqual } from "lodash";
import queryString from "query-string";

import { SWReactIcons } from "@similarweb/icons";
import type { GenericGraphProps } from "@similarweb/pro-logic-components";
import { apiFormat, dayAndShortMonth, fullMonthAndYear, shortMonthAndYear } from "@similarweb/pro-logic-components";
import { PlainTooltip } from "@similarweb/ui-components";

import type { Filter } from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import {
    isDurationSwitcherLocked,
    updateGraphGranularityValue,
} from "app/common/keyword-research/keywords-overview/trendOverTimeTimeframeHelpers";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { BasicDurationValues } from "app/services/DurationService";
import DurationService from "app/services/DurationService";
import { openHooksModalV5 } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { addPartialDataZones } from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";

import {
    baseMetricItemsConfig,
    EXCEL_URL,
    GRANULARITY_SWITCHER_OPTIONS,
    DURATION_URL_PARAM_KEY,
    GRANULARITY_URL_PARAM_KEY,
    legendIdToTextMap,
} from "app/common/keyword-research/keywords-overview/trendOverTimeConsts";
import { LegendValue } from "./TrendOverTimeStyledComponents";
import type {
    AverageSplitByMetric,
    DataSplitByMetric,
    IAverage,
    IBreakdown,
    IGraphItem,
    ISwitcherItem,
    ITrendOverTimeResponseData,
} from "app/common/keyword-research/keywords-overview/trendOverTimeTypes";

export const yAxisFormatter = ({ value }) => abbrNumberFilter()(value, true);
export const xAxisFormatter = ({ value }) => dayjs.utc(value).format(dayAndShortMonth);
export const legendFormatter = (val: number | string, legendItem: IGraphItem) =>
    !val ? (
        "-"
    ) : (
        <FlexRow>
            <LegendValue>{abbrNumberFilter()(val, true, 2)}</LegendValue>
            <PlainTooltip tooltipContent={translate(legendIdToTextMap[legendItem.id])}>
                <div>
                    <SWReactIcons
                        iconName="info"
                        size="xs"
                    />
                </div>
            </PlainTooltip>
        </FlexRow>
    );

export const useSwitcherItems = (
    switcherOptions = GRANULARITY_SWITCHER_OPTIONS,
    conditionalParam?: unknown,
    itemsUpdater?: (conditionalParam: unknown, switcherOptions: ISwitcherItem[]) => ISwitcherItem[],
): { switcherItems: ISwitcherItem[] } => {
    const [switcherItems, setSwitcherItems] = useState<ISwitcherItem[]>(switcherOptions);

    useLayoutEffect(() => {
        if (conditionalParam && itemsUpdater) {
            const updatedSwitcherItems = itemsUpdater(conditionalParam, switcherOptions);
            if (!isEqual(updatedSwitcherItems, switcherItems)) {
                setSwitcherItems(updatedSwitcherItems);
            }
        }
    }, [JSON.stringify(conditionalParam)]);

    return { switcherItems };
};

export const trackSwitcherItemClick = (selectedGranularity: string) =>
    allTrackers.dispatchTrackEvent("Switch", "switch", `Graph Switcher Change${selectedGranularity}`);

export const onGranularitySwitcherItemClick = (switcheritems: ISwitcherItem[]) => (selectedIndex: number) => {
    const selectedItemValue = switcheritems[selectedIndex].value as GranularityNames;

    trackSwitcherItemClick(selectedItemValue);

    proNavigator.updateQueryParams(
        {
            [GRANULARITY_URL_PARAM_KEY]: selectedItemValue,
        },
        { reload: false },
    );
};

export const onDurationSwitcherItemClick =
    (switcheritems: ISwitcherItem[], graphGranularity: GranularityNames) => (selectedIndex: number) => {
        const selectedItemValue = switcheritems[selectedIndex].value as BasicDurationValues;
        if (isDurationSwitcherLocked(selectedItemValue)) {
            openHooksModalV5("keywordResearch");
            return;
        }
        trackSwitcherItemClick(selectedItemValue);

        const granularityParamUpdate = updateGraphGranularityValue(
            selectedItemValue,
            graphGranularity,
            GRANULARITY_URL_PARAM_KEY,
        );

        proNavigator.updateQueryParams(
            {
                [DURATION_URL_PARAM_KEY]: selectedItemValue,
                ...(granularityParamUpdate && granularityParamUpdate),
            },
            { reload: false },
        );
    };

const getData = (data: number[][], hasPartialWeek = false) =>
    data.map(([date, value], index) => ({
        x: date,
        y: value,
        isPartial: index === data.length - 1 ? hasPartialWeek : false,
    }));

const getDataSplitByMetric = (Breakdown: IBreakdown, showSourceTypeBreakdown: boolean): DataSplitByMetric => {
    return Object.entries(Breakdown).reduce(
        (acc, [date, data]) => {
            const {
                Clicks: { Organic, Paid },
                Volume,
            } = data;
            const dateValue = dayjs.utc(date).valueOf();

            if (showSourceTypeBreakdown) {
                return {
                    Volume: [...acc.Volume, [dateValue, Volume]],
                    Organic: [...acc.Organic, [dateValue, Organic]],
                    Paid: [...acc.Paid, [dateValue, Paid]],
                };
            } else {
                return {
                    Volume: [...acc.Volume, [dateValue, Volume]],
                    Clicks: [...acc.Clicks, [dateValue, Organic + Paid]],
                };
            }
        },
        { Volume: [], Clicks: [], Organic: [], Paid: [] } as any,
    );
};

const getAverageSplitByMetric = (Average: IAverage, showSourceTypeBreakdown: boolean): AverageSplitByMetric => {
    const {
        Clicks: { Organic, Paid },
        Volume,
    } = Average;
    if (showSourceTypeBreakdown) {
        return {
            Volume,
            Organic,
            Paid,
        };
    }
    return {
        Volume,
        Clicks: Organic + Paid,
    };
};

export const getGraphItems = (
    rawData: ITrendOverTimeResponseData,
    hasPartialWeek = false,
    showSourceTypeBreakdown = false,
): GenericGraphProps => {
    const { Breakdown, Average } = rawData;
    const dataSplitByMetric = getDataSplitByMetric(Breakdown, showSourceTypeBreakdown);
    const averageSplitByMetric = getAverageSplitByMetric(Average, showSourceTypeBreakdown);

    const graphItems = baseMetricItemsConfig(showSourceTypeBreakdown).map((config) => {
        const { id, name } = config;
        const data = getData(dataSplitByMetric[id], hasPartialWeek);
        const total = averageSplitByMetric[id];
        return {
            ...config,
            name: translate(name),
            data,
            total,
        };
    });
    return addPartialDataZones(graphItems);
};

const getFromDate = (graphDuration: string, toDate: Dayjs) => {
    const { unit, count } = DurationService.parsedDurationParam(graphDuration);
    return toDate.subtract(count - 1, unit).startOf("month");
};

export const getUpdatedSubtitleFilters = (subtitleFilters: Filter[], graphDuration: string, to: Dayjs): Filter[] => {
    const updatedSubtitleFilters = [...subtitleFilters];
    const isOneMonthSelected = DurationService.isLessThanOrEqualToOneMonths(graphDuration);
    const format = isOneMonthSelected ? fullMonthAndYear : shortMonthAndYear;
    const from = getFromDate(graphDuration, to);
    const updatedDateObject = {
        filter: "date",
        value: {
            from: from.format(apiFormat),
            to: to.format(apiFormat),
            useRangeDisplay: !isOneMonthSelected,
        },
        format,
    };

    updatedSubtitleFilters.splice(0, 1, updatedDateObject);
    return updatedSubtitleFilters;
};

export const getExcelUrl = (apiParams: Record<string, string | boolean>) => {
    const queryStringParams = queryString.stringify(apiParams);
    return `${EXCEL_URL}?${queryStringParams}`;
};
